import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { fetch_payout_details_awaiting, fetch_payout_details_pending } from '../api';
import { awaiting_view, pending_view, review_error, since_month_error } from './adapters';
import ReviewDialog from './review_dialog';
import './payment_details.css';

/*
 * the Payment details tab (/teacher-payments/payment-details): the submissions waiting for review
 * (oldest first, paged by next_after_id) and the teachers with no approved details.
 */
const PENDING_FAILED_TEXT = 'Could not load the payment details waiting for review.';
const AWAITING_FAILED_TEXT = 'Could not load the teachers awaiting payment details.';
const VIEW_PENDING = 'pending';
const VIEW_AWAITING = 'awaiting';

const Teacher = ({ r }) => (
  <div>
    <div>{r.teacher_name}</div>
    {r.teacher_email ? <div className="tp-hint">{r.teacher_email}</div> : null}
  </div>
);

const pending_columns = (on_review) => [
  { key: 'teacher', label: 'Teacher', hideInCard: true, render: (r) => <Teacher r={r} /> },
  { key: 'submitted_at', label: 'Submitted (IST)' },
  {
    key: 'holder',
    label: 'Account holder',
    render: (r) => (
      <div>
        <div>{r.account_holder_name}</div>
        <StatusPill tone={r.name_match.tone}>{r.name_match.label}</StatusPill>
      </div>
    )
  },
  {
    key: 'account',
    label: 'Account',
    render: (r) => (
      <div>
        <div className="tp-mono">{r.account_number_masked}</div>
        <div className="tp-hint">
          {r.ifsc} · {r.bank_name}
        </div>
      </div>
    )
  },
  {
    key: 'tax',
    label: 'Tax',
    render: (r) => (
      <div>
        <div>GST {r.gst_registered ? r.gst_rate : 'not registered'}</div>
        <div>TDS {r.tds_rate}</div>
      </div>
    )
  },
  {
    key: 'flags',
    label: 'Notes',
    render: (r) => (
      <div className="tp-pd-pills">
        {r.is_change ? <StatusPill tone="blue">Replaces approved details</StatusPill> : null}
        {r.shared_account ? <StatusPill tone="red">Shared account</StatusPill> : null}
        {!r.is_change && !r.shared_account ? (
          <span className="tp-hint">First submission</span>
        ) : null}
      </div>
    )
  },
  {
    key: 'actions',
    label: 'Action',
    hideInCard: true,
    render: (r) => (
      <button
        type="button"
        className="tp-link"
        aria-label={`Review payment details of ${r.teacher_name}`}
        onClick={() => on_review(r.detail_uuid)}
      >
        Review
      </button>
    )
  }
];

const awaiting_columns = (on_review) => [
  { key: 'teacher', label: 'Teacher', hideInCard: true, render: (r) => <Teacher r={r} /> },
  { key: 'latest_month', label: 'Latest payment month' },
  {
    key: 'details',
    label: 'Payment details',
    render: (r) =>
      r.pending_detail_uuid ? (
        <span>
          Submitted, waiting for review.{' '}
          <button
            type="button"
            className="tp-link"
            aria-label={`Review payment details of ${r.teacher_name}`}
            onClick={() => on_review(r.pending_detail_uuid)}
          >
            Review
          </button>
        </span>
      ) : (
        <span className="tp-hint">Not submitted yet</span>
      )
  }
];

// One cursor-paged list: rows append on "Load more"; a reload starts over. read(cursor) -> {rows, next}.
const usePagedList = (read, failed_text, on_error) => {
  const [state, setState] = useState({ status: 'loading', rows: [], next: null, meta: {} });
  const [more_busy, setMoreBusy] = useState(false);
  const [error, setError] = useState({ kind: '', text: '' });
  const seq = useRef(0);

  const run = useCallback(
    (cursor) => {
      seq.current += 1;
      const mine = seq.current;
      if (cursor) setMoreBusy(true);
      else setState((s) => ({ ...s, status: 'loading' }));
      read(cursor)
        .then((page) => {
          if (mine !== seq.current) return;
          setMoreBusy(false);
          setError({ kind: '', text: '' });
          setState((s) => ({
            status: 'ready',
            rows: cursor ? s.rows.concat(page.rows) : page.rows,
            next: page.next,
            meta: page.meta || {}
          }));
        })
        .catch((e) => {
          if (mine !== seq.current) return;
          const v = review_error(e, failed_text);
          if (v.kind === 'auth') on_error(e, failed_text);
          setMoreBusy(false);
          setError(v);
          // A failed "Load more" keeps the rows already shown.
          if (!cursor) setState({ status: 'error', rows: [], next: null, meta: {} });
        });
    },
    [read, failed_text, on_error]
  );

  useEffect(() => {
    run(null);
  }, [run]);

  return { ...state, more_busy, error, reload: () => run(null), load_more: () => run(state.next) };
};

const read_pending = (cursor) =>
  fetch_payout_details_pending(cursor).then((d) => {
    const v = pending_view(d);
    return { rows: v.rows, next: v.next_after_id };
  });

const ListBody = ({ list, columns, rowKey, empty_text, title_of }) => {
  if (list.status === 'loading') return <CustomLoading />;
  if (list.status === 'error') {
    return list.error.kind === 'forbidden' ? (
      <div className="tp-banner tp-banner-info">{list.error.text}</div>
    ) : (
      <LoadFailed text={list.error.text} on_retry={list.reload} />
    );
  }
  return (
    <>
      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={rowKey}
        emptyText={empty_text}
        cardTitle={(r) => <b>{title_of(r)}</b>}
      />
      {list.error.text ? <LoadFailed text={list.error.text} on_retry={list.load_more} /> : null}
      {list.next ? (
        <div className="tp-pd-more">
          <button
            type="button"
            className="tp-btn-out"
            onClick={list.more_busy ? undefined : list.load_more}
            aria-disabled={list.more_busy ? 'true' : undefined}
          >
            {list.more_busy ? 'Loading…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </>
  );
};

const PaymentDetailsTab = ({ on_error, on_notify }) => {
  const [view, setView] = useState(VIEW_PENDING);
  const [since_draft, setSinceDraft] = useState('');
  const [since_month, setSinceMonth] = useState('');
  const [reviewing, setReviewing] = useState('');

  const read_awaiting = useCallback(
    (cursor) =>
      fetch_payout_details_awaiting(since_month, cursor).then((d) => {
        const v = awaiting_view(d);
        return {
          rows: v.rows,
          next: v.next_after_teacher_id,
          meta: { since_month: v.since_month }
        };
      }),
    [since_month]
  );

  const pending = usePagedList(read_pending, PENDING_FAILED_TEXT, on_error);
  const awaiting = usePagedList(read_awaiting, AWAITING_FAILED_TEXT, on_error);
  const since_err = since_month_error(since_draft);

  const reload_all = () => {
    pending.reload();
    awaiting.reload();
  };

  const on_reviewed = (text) => {
    setReviewing('');
    on_notify('success', text);
    reload_all();
  };

  const on_stale = () => {
    setReviewing('');
    reload_all();
  };

  return (
    <div className="tp-pd-tab">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" role="heading" aria-level={2}>
          Payment details
        </span>
        <span className="tp-card-hd-sub">
          Bank and tax details teachers submit. Nobody edits them: approve or reject each one.
        </span>
      </div>

      <div className="tp-seg" role="group" aria-label="Payment details list">
        <button
          type="button"
          className={view === VIEW_PENDING ? 'on' : ''}
          aria-pressed={view === VIEW_PENDING}
          onClick={() => setView(VIEW_PENDING)}
        >
          Waiting for review
          {pending.status === 'ready' ? ` (${pending.rows.length}${pending.next ? '+' : ''})` : ''}
        </button>
        <button
          type="button"
          className={view === VIEW_AWAITING ? 'on' : ''}
          aria-pressed={view === VIEW_AWAITING}
          onClick={() => setView(VIEW_AWAITING)}
        >
          Awaiting details
        </button>
      </div>

      {view === VIEW_PENDING ? (
        <>
          <div className="tp-hint tp-pd-intro">Oldest first. Review each one before payouts.</div>
          <ListBody
            list={pending}
            columns={pending_columns(setReviewing)}
            rowKey={(r) => r.detail_uuid}
            empty_text="No payment details are waiting for review."
            title_of={(r) => r.teacher_name}
          />
        </>
      ) : (
        <>
          <form
            className="tp-frow"
            onSubmit={(e) => {
              e.preventDefault();
              if (!since_err) setSinceMonth(since_draft.trim());
            }}
          >
            <label htmlFor="tp-pd-since" className="tp-hint">
              Teachers with a payment month since (YYYY-MM, blank for the default)
            </label>
            <input
              id="tp-pd-since"
              className="tp-input"
              value={since_draft}
              onChange={(e) => setSinceDraft(e.target.value)}
              placeholder="2026-10"
            />
            <button
              type="submit"
              className="tp-btn-out"
              aria-disabled={since_err ? 'true' : undefined}
            >
              Show
            </button>
            {since_err ? (
              <span className="tp-neg" role="alert">
                {since_err}
              </span>
            ) : null}
          </form>
          {awaiting.status === 'ready' && awaiting.meta.since_month ? (
            <div className="tp-hint tp-pd-intro">
              Teachers with a payment month since {awaiting.meta.since_month} and no approved
              payment details. They cannot be paid until their details are approved.
            </div>
          ) : null}
          <ListBody
            list={awaiting}
            columns={awaiting_columns(setReviewing)}
            rowKey={(r) => r.teacher_uuid}
            empty_text="Every teacher with a payment month has approved payment details."
            title_of={(r) => r.teacher_name}
          />
        </>
      )}

      {reviewing ? (
        <ReviewDialog
          detail_uuid={reviewing}
          on_close={() => setReviewing('')}
          on_reviewed={on_reviewed}
          on_stale={on_stale}
          on_error={on_error}
        />
      ) : null}
    </div>
  );
};

export default PaymentDetailsTab;
